import React from 'react';

const PUBLIC_URL = process.env.PUBLIC_URL || '';

/**
 * Normalisasi string nama bank agar toleran terhadap variasi pengetikan
 */
export function normalizeBankCode(rawName) {
  if (!rawName) return 'OTHER';
  const clean = String(rawName).trim().toLowerCase().replace(/[^a-z0-9]/g, '');

  if (clean.includes('bca')) return 'BCA';
  if (clean.includes('mandiri') || clean.includes('livin')) return 'MANDIRI';
  if (clean.includes('bri') || clean.includes('brimo')) return 'BRI';
  if (clean.includes('bni')) return 'BNI';
  if (clean.includes('bsi') || clean.includes('syariahindonesia')) return 'BSI';
  if (clean.includes('cimb') || clean.includes('niaga')) return 'CIMB';
  if (clean.includes('permata')) return 'PERMATA';
  if (clean.includes('danamon')) return 'DANAMON';
  if (clean.includes('btn')) return 'BTN';
  if (clean.includes('panin')) return 'PANIN';
  if (clean.includes('jago')) return 'JAGO';
  if (clean.includes('seabank') || clean.includes('sea')) return 'SEABANK';
  if (clean.includes('jenius') || clean.includes('btpn')) return 'JENIUS';
  if (clean.includes('qris')) return 'QRIS';
  if (clean.includes('visa')) return 'VISA';
  if (clean.includes('mastercard') || clean === 'master') return 'MASTERCARD';
  if (clean.includes('gpn')) return 'GPN';

  return 'OTHER';
}

/**
 * Registry Logo Resmi Bank & Payment Gateway
 * Menggunakan aset vektor SVG resmi asli yang disajikan dari folder /banks/ di public
 */
export const OFFICIAL_BANK_REGISTRY = {
  BCA: { id: 'BCA', label: 'BCA', src: `${PUBLIC_URL}/banks/bca.svg` },
  MANDIRI: { id: 'MANDIRI', label: 'Mandiri', src: `${PUBLIC_URL}/banks/mandiri.svg` },
  BRI: { id: 'BRI', label: 'BRI', src: `${PUBLIC_URL}/banks/bri.svg` },
  BNI: { id: 'BNI', label: 'BNI', src: `${PUBLIC_URL}/banks/bni.svg` },
  BSI: { id: 'BSI', label: 'BSI', src: `${PUBLIC_URL}/banks/bsi.svg` },
  CIMB: { id: 'CIMB', label: 'CIMB Niaga', src: `${PUBLIC_URL}/banks/cimb.svg` },
  PERMATA: { id: 'PERMATA', label: 'Permata', src: `${PUBLIC_URL}/banks/permata.svg` },
  DANAMON: { id: 'DANAMON', label: 'Danamon', src: `${PUBLIC_URL}/banks/danamon.svg` },
  BTN: { id: 'BTN', label: 'BTN', src: `${PUBLIC_URL}/banks/btn.svg` },
  PANIN: { id: 'PANIN', label: 'Panin', src: `${PUBLIC_URL}/banks/panin.svg` },
  JAGO: { id: 'JAGO', label: 'Bank Jago', src: `${PUBLIC_URL}/banks/jago.svg` },
  SEABANK: { id: 'SEABANK', label: 'SeaBank', src: `${PUBLIC_URL}/banks/seabank.svg` },
  JENIUS: { id: 'JENIUS', label: 'Jenius', src: `${PUBLIC_URL}/banks/jenius.svg` },
  QRIS: { id: 'QRIS', label: 'QRIS', src: `${PUBLIC_URL}/banks/qris.svg` },
  VISA: { id: 'VISA', label: 'VISA', src: `${PUBLIC_URL}/banks/visa.svg` },
  MASTERCARD: { id: 'MASTERCARD', label: 'Mastercard', src: `${PUBLIC_URL}/banks/mastercard.svg` },
  GPN: { id: 'GPN', label: 'GPN', src: `${PUBLIC_URL}/banks/gpn.svg` },
};

/**
 * Komponen BankLogo
 * Menampilkan logo resmi asli bank dengan kontainer putih bersih agar kontras dan terbaca di semua tema (light / dark).
 */
export default function BankLogo({
  name,
  className = '',
  size = 'md',
  showContainer = true,
  alt = ''
}) {
  const code = normalizeBankCode(name);
  const bank = OFFICIAL_BANK_REGISTRY[code];

  // Preset tinggi logo
  const heightClasses = {
    xs: 'h-3.5 max-h-3.5',
    sm: 'h-4.5 max-h-4.5',
    md: 'h-5.5 max-h-5.5',
    lg: 'h-7 max-h-7',
  }[size] || 'h-5 max-h-5';

  if (bank?.src) {
    if (!showContainer) {
      return (
        <img
          src={bank.src}
          alt={alt || bank.label}
          className={`${heightClasses} w-auto object-contain ${className}`}
          loading="lazy"
        />
      );
    }

    return (
      <div
        className={`inline-flex items-center justify-center bg-white rounded-md px-1.5 py-0.5 border border-slate-200/90 shadow-2xs shrink-0 ${className}`}
      >
        <img
          src={bank.src}
          alt={alt || bank.label}
          className={`${heightClasses} w-auto object-contain`}
          loading="lazy"
        />
      </div>
    );
  }

  // Fallback jika bank lokal/custom
  return (
    <div
      className={`inline-flex items-center gap-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 rounded-md px-2 py-0.5 border border-slate-300 dark:border-slate-700 text-xs font-bold uppercase shrink-0 ${className}`}
    >
      <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
      <span>{name || 'BANK'}</span>
    </div>
  );
}
